import { FC } from "react";
import { Box, Button, IconButton, Skeleton } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import { TFunction } from "i18next";

type GroupCellProps = {
  params: any;
  t: TFunction;
  onAssign: (id: string) => void;
  onAssignToUser?: (userId: string) => void;
  onToggle?: (id: string, expanded: boolean) => void;
};

export const GroupCell: FC<GroupCellProps> = ({
  params,
  t,
  onAssign,
  onAssignToUser,
  onToggle,
}) => {
  const apiRef = useGridApiContext();
  const rowNode = params.rowNode;

  // Leaf row: license child OR user-without-license flat row
  if (rowNode.type !== "group") {
    // User-without-license rows have no license, check this first
    if (params.row?.isUserWithNoLicense) {
      return (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <span style={{ fontWeight: 400 }}>{params.row.username}</span>
        </Box>
      );
    }

    const license = params.row?.license;

    if (!license) return null;

    if (license?.ownerGroupId && !license?.username) {
      return <Skeleton variant="text" width="60%" height={40} />;
    }

    if (license?.username) return null;

    return (
      <Button
        onClick={() => onAssign(String(params.id))}
        sx={{ textTransform: "none" }}
        disabled={new Date(license.expiryDate).getTime() - Date.now() <= 0}
      >
        {t("licenses.assignUser")}
      </Button>
    );
  }

  const isExpanded = rowNode.childrenExpanded;

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !isExpanded;
    apiRef.current.setRowChildrenExpansion(params.id, next);
    onToggle?.(String(params.id), next);
  };

  return (
    <Box
      sx={{ display: "flex", alignItems: "center" }}
      onClick={(e) => e.stopPropagation()}
    >
      <IconButton size="small" onClick={toggle}>
        {isExpanded ? (
          <KeyboardArrowDown fontSize="small" />
        ) : (
          <KeyboardArrowRight fontSize="small" />
        )}
      </IconButton>
      <span>
        {rowNode.groupingKey}{" "}
        <span style={{ color: "#888", fontSize: "0.85em" }}>
          ({rowNode.children?.length ?? 0})
        </span>
      </span>
    </Box>
  );
};
